import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import html2pdf from 'html2pdf.js';
import { getMembres } from '../services/api';
import { notificationService } from '../utils/notifications';
import './ListeMontantsPrint.css';

const ListeMembresPrint = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const printRef = useRef(null);
  const [membres, setMembres] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedAssociation, setSelectedAssociation] = useState('');

  // Récupérer les paramètres depuis l'URL
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const search = params.get('search') || '';
    const association = params.get('association') || '';
    setSearchTerm(search);
    setSelectedAssociation(association);
  }, [location]);

  useEffect(() => {
    const loadMembres = async () => {
      try {
        const response = await getMembres();
        setMembres(response.data || []);
      } catch (error) {
        console.error('Erreur lors du chargement des membres:', error);
        notificationService.error('Erreur', 'Impossible de charger les membres');
      } finally {
        setLoading(false);
      }
    };

    loadMembres();
  }, []);

  const handlePrint = () => {
    setTimeout(() => {
      window.print();
    }, 100);
  };

  const handleDownloadPDF = async () => {
    if (!printRef.current) return;

    try {
      const opt = {
        margin: [10, 10, 10, 10],
        filename: `Liste_Membres${selectedAssociation ? `_${selectedAssociation.replace(/\s+/g, '_')}` : ''}_${new Date().toISOString().split('T')[0]}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { 
          scale: 2,
          useCORS: true,
          logging: false,
          backgroundColor: '#ffffff',
          width: printRef.current.scrollWidth,
          height: printRef.current.scrollHeight,
          windowWidth: printRef.current.scrollWidth,
          allowTaint: true
        },
        jsPDF: { 
          unit: 'mm', 
          format: 'a4', 
          orientation: 'landscape'
        },
        pagebreak: { mode: ['avoid-all', 'css', 'legacy'], avoid: ['.print-header', '.print-table'] }
      };

      await html2pdf().set(opt).from(printRef.current).save();
      notificationService.success('PDF généré', 'Le fichier PDF a été téléchargé avec succès.');
    } catch (error) {
      console.error('Erreur lors de la génération du PDF:', error);
      notificationService.error('Erreur', 'Erreur lors de la génération du PDF. Veuillez réessayer.');
    }
  };

  const handleClose = () => {
    window.close();
    if (!document.hidden) {
      navigate('/admin');
    }
  };

  // Filtrer les membres
  const filteredMembres = membres.filter(membre => {
    // Filtre par association
    if (selectedAssociation && membre.association !== selectedAssociation) {
      return false;
    }
    
    // Filtre par recherche
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      (membre.nom && membre.nom.toLowerCase().includes(search)) ||
      (membre.postnom && membre.postnom.toLowerCase().includes(search)) ||
      (membre.prenom && membre.prenom.toLowerCase().includes(search)) ||
      (membre.numero_matricule && membre.numero_matricule.toLowerCase().includes(search)) ||
      (membre.telephone && membre.telephone.includes(search)) ||
      (membre.association && membre.association.toLowerCase().includes(search)) ||
      (membre.quartier && membre.quartier.toLowerCase().includes(search)) ||
      (membre.commune && membre.commune.toLowerCase().includes(search)) ||
      (membre.nom_recolteur && membre.nom_recolteur.toLowerCase().includes(search)) ||
      (membre.centre_sante && membre.centre_sante.toLowerCase().includes(search))
    );
  });

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Chargement des membres...</p>
      </div>
    );
  }

  return (
    <div className="liste-montants-print-page" style={{ display: 'block', visibility: 'visible' }}>
      {/* Barre d'outils - masquée à l'impression */}
      <div className="print-toolbar" style={{ display: 'flex' }}>
        <div className="toolbar-left">
          <h2>Liste des Membres - Impression{selectedAssociation ? ` - ${selectedAssociation}` : ''}</h2>
        </div>
        <div className="toolbar-right">
          <button className="btn btn-primary" onClick={handlePrint}>
            🖨️ Imprimer
          </button>
          <button className="btn btn-success" onClick={handleDownloadPDF}>
            📥 Télécharger PDF
          </button>
          <button className="btn btn-secondary" onClick={handleClose}>
            ✕ Fermer
          </button>
        </div>
      </div>

      {/* Contenu à imprimer */}
      <div ref={printRef} className="print-content" style={{ display: 'block', visibility: 'visible', background: 'white' }}>
        {/* En-tête */}
        <div className="print-header">
          <div className="header-content-wrapper">
            <div className="header-logo">
              <img src="/logo.png" alt="Logo" className="logo-img" />
            </div>
            <div className="header-text">
              <h1>MUTU NDE ESPOIR ASBL</h1>
              <h2>Liste des Membres{selectedAssociation ? ` - ${selectedAssociation}` : ''}</h2>
              <div className="header-info">
                <p className="print-date">
                  <strong>Date d'édition :</strong> {new Date().toLocaleDateString('fr-FR', { 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </p>
                {selectedAssociation && (
                  <p className="print-date" style={{ marginTop: '5px' }}>
                    <strong>Association :</strong> {selectedAssociation}
                  </p>
                )}
                <p className="print-date" style={{ marginTop: '5px' }}>
                  <strong>Total :</strong> {filteredMembres.length} membre(s)
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Tableau */}
        <div className="print-table-container">
          <table className="print-table">
            <thead>
              <tr>
                <th>N°</th>
                <th>Matricule</th>
                <th>Nom complet</th>
                <th>Date adhésion</th>
                <th>Téléphone</th>
                <th>Centre de santé</th>
                <th>Commune</th>
              </tr>
            </thead>
            <tbody>
              {filteredMembres.length === 0 ? (
                <tr>
                  <td colSpan="7" style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
                    {searchTerm || selectedAssociation ? 'Aucun membre trouvé' : 'Aucun membre enregistré'}
                  </td>
                </tr>
              ) : (
                filteredMembres.map((membre, index) => (
                  <tr key={membre.id}>
                    <td>{index + 1}</td>
                    <td>{membre.numero_matricule || '-'}</td>
                    <td>{membre.nom} {membre.postnom || ''} {membre.prenom}</td>
                    <td>{membre.date_adhesion ? new Date(membre.date_adhesion).toLocaleDateString('fr-FR') : '-'}</td>
                    <td>{membre.telephone || '-'}</td>
                    <td>{membre.centre_sante || '-'}</td>
                    <td>{membre.commune || '-'}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pied de page */}
        <div className="print-footer">
          <p>Document généré par MUTU NDE ESPOIR ASBL - Système de gestion</p>
        </div>
      </div>
    </div>
  );
};

export default ListeMembresPrint;

